import {
  ComparatorContext,
  KiziLogger,
  ProductType,
  Review,
  useCurrentStepIndex,
  useField,
  useFields,
  usePage,
  useWizardService,
} from '@kizi/comparator-core';
import { Button, Div, Image } from '@kizi/core-components';
import { IconV2 as Icon } from '@kizi/shared-components';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { Price, RatingReviews } from '..';
import { COMPARATOR_TYPE } from '../../funnels';
import {
  SORTING_ASCENDING_ORDER,
  SORTING_OPTIONS,
  SORTING_REFERENCE,
  addFiltersFromData,
} from '../../funnels/energy';
import { Desktop, Mobile } from '../../utils/breakpoint';
import { prepareFiltersObject } from '../../utils/filters';
import { isMobile, sortArrayByKey } from '../../utils/global';
import { EnergyProduct, getProductProps } from '../../utils/product';
import { Choices, FiltersProps } from '../Filters';
import { PageLayout, PageLayoutProps } from '../PageLayout';
import { useReviews } from '../hooks/reviews';
import { OverviewPageLoader } from './OverviewPageLoader';

export const filterProducts = (
  products: EnergyProduct[],
  filters: FiltersProps['choices'],
): EnergyProduct[] => {
  const updatedProducts: EnergyProduct[] = [];
  for (const product of products) {
    let shouldBeAdded = true;
    for (const filterName in product.filters) {
      const productFilterValue = product.filters[filterName];
      const selectedFilters = filters[filterName] ?? [];

      if (
        selectedFilters.length > 0 &&
        selectedFilters[0] !== 'all' &&
        !selectedFilters.includes(productFilterValue) &&
        !selectedFilters.includes('' + productFilterValue)
      ) {
        shouldBeAdded = false;
      }
    }
    if (shouldBeAdded) updatedProducts.push(product);
  }
  return updatedProducts;
};

export interface OverviewPageProps extends PageLayoutProps {
  productsRequest: {
    url: string;
    options?: {
      method?: string;
      headers?: any;
      body?: any;
      queryParams?: { [key: string]: string };
    };
    transformer: (data: any) => EnergyProduct[];
  };
  choices?: FiltersProps['choices'];
  paramsFromFields?: string[];
  defaultQueryParams?: {
    limit?: number;
    tariff?: string;
    sustainable?: string;
    housenumberaddition?: string;
    electricitycapacity?: number;
    gascapacity?: number;
    smartmeter?: number;
    customertype?: string;
    supplytype?: string;
    withsavings?: number;
    pricesexcludingtax?: boolean;
    hideunavailableproducts?: boolean;
    type?: string;
  };
}

function getSortingOptions(hasSolarPanels = false) {
  const options = [
    {
      label: 'Laagste prijs',
      icon: 'money',
      value: SORTING_OPTIONS.Price,
    },
  ];

  if (hasSolarPanels) {
    options.splice(1, 0, {
      label: 'Hoogste teruglevertarief',
      icon: 'money',
      value: SORTING_OPTIONS.Highest_Redelivery_Tariff,
    });
  }

  return options;
}

export interface Consumption {
  electricitysupplyhigh: number;
  electricitysupplylow: number;
  electricityusagehigh: string;
  electricityusagelow: string;
  gasusage: string;
}

function getConsumption(data): Consumption {
  return _.pick(data, [
    'electricitysupplyhigh',
    'electricitysupplylow',
    'electricityusagehigh',
    'electricityusagelow',
    'gasusage',
  ]);
}

export const OverviewPage: React.FC<OverviewPageProps> = ({
  productsRequest,
  content,
  sideNav,
  choices = {},
  defaultQueryParams = {},
  paramsFromFields,
  ...props
}) => {
  const { state, setState } = usePage();
  const [currentSorting, setCurrentSorting] = useState(
    getSortingOptions()[0].value,
  );
  const [selectedFilters, setSelectedFilters] = useState(choices);
  const fields = useFields(paramsFromFields ?? []);

  const [visibleProducts, setVisibleProducts] = useState<EnergyProduct[]>([]);

  const { reviews, isLoading } = useReviews(ProductType.ENERGY);
  const currentStepIndex = useCurrentStepIndex();
  const service = useWizardService();
  const { getStateFromLocalStorage, persistStateInLocalStorage } =
    useContext(ComparatorContext);
  const requestLogField = useField('overview.requestLogId');
  const comparisonProductsFiled = useField('overview.comparisonProducts');
  const filtersField = useField('overview.filters');
  const comparisonProducts = JSON.parse(
    comparisonProductsFiled?.value
      ? (comparisonProductsFiled?.value as string)
      : '[]',
  );
  const wizardStepIndex = useCurrentStepIndex();
  const fieldValueChange = (name: string, value: string) => {
    value &&
      name &&
      name.length > 0 &&
      service?.send({
        type: 'CHANGE',
        target: { name: name, value: value },
      });
  };

  const loadComparisonProducts = (products: EnergyProduct[]) => {
    if (comparisonProducts && comparisonProducts.length > 0) {
      const tempProducts: EnergyProduct[] =
        isMobile() && comparisonProducts.length > 2
          ? comparisonProducts.slice(0, comparisonProducts.length - 1)
          : comparisonProducts;

      tempProducts.forEach((p) => {
        const productIndex = products.findIndex((f) => f.id === p.id);
        if (productIndex >= 0) {
          products[productIndex].compare = true;
        }
      });
    }
  };

  if (
    !setState ||
    !service ||
    !service.state.context.steps ||
    currentStepIndex === undefined
  )
    return null;
  const overviewIndex = service.state.context.steps.findIndex(
    (step) => step.id === 'overview',
  );
  const interpreter = service.state.context.steps[overviewIndex];

  // TODO: remove this and trigger a Step change on Product selection
  interpreter.subscribe((s) => {
    if (s.event.type === 'done.invoke.onChangeAsynchronousValidation') {
      const persistedState = getStateFromLocalStorage();
      if (!persistedState || !persistedState.context) return;

      persistedState.context.initialSteps[currentStepIndex].fields =
        s.context.fields;
      persistStateInLocalStorage(persistedState);
    }
  });

  const handleChange = (products) => (values: Choices) => {
    const encodedData = JSON.stringify(values);
    const allFields = [
      ...(interpreter.getSnapshot()?.context.fields || []),
      ...(interpreter.getSnapshot()?.context.externalFields || []),
    ];
    const filters = allFields?.findIndex((f) => f.name.includes('filters'));
    if (filters !== undefined && filters > -1) {
      filtersField?.set({ value: encodedData });
      service?.send([
        {
          type: 'CHANGE',
          target: { name: 'filters', value: encodedData },
        },
      ]);
    }

    setSelectedFilters(values);
    setVisibleProducts(filterProducts(products, values));
  };

  useEffect(() => {
    setState({ ...state, productsLoading: true });
    const queryParams = {};
    for (const field of fields) {
      if (field) queryParams[field.name] = field.value ?? '';
    }
    const finalQueryParams = { ...defaultQueryParams, ...queryParams };
    let index = 0;
    let url =
      productsRequest.url.length[productsRequest.url.length - 1] === '/'
        ? productsRequest.url
        : productsRequest.url + '/';
    for (const key in finalQueryParams) {
      let separator = '&';
      if (index === 0) separator = '?';
      url = url + separator + key + '=' + finalQueryParams[key];
      index++;
    }

    fetch(url, { ...productsRequest.options })
      .then((res) => res.json())
      .then((body) => {
        requestLogField?.onChange?.({
          target: {
            name: 'requestLogId',
            value: body.meta.requestLogId,
          },
        } as React.ChangeEvent<any>);
        const productsFromApi = addFiltersFromData(
          productsRequest.transformer(body),
          getConsumption(finalQueryParams),
        );
        for (const prod of productsFromApi) {
          if (!prod.type) {
            prod.type = 'energy';
          }
        }
        const { choices: choicesFromProducts, groups } = prepareFiltersObject(
          productsFromApi,
          COMPARATOR_TYPE.ENERGY,
        );

        loadComparisonProducts(productsFromApi);

        setState({
          ...state,
          availableFilters: groups,
          onChange: handleChange(productsFromApi),
          onItemClick: setCurrentSorting,
          onCompareChange: handleCompareChange(productsFromApi),
          options: getSortingOptions(
            finalQueryParams['electricitysupplyhigh'] > 0 ||
              finalQueryParams['electricitysupplylow'] > 0,
          ),
          selected: currentSorting,
          productsLoading: false,
        });

        setCurrentSorting(0);

        const encodedFilters = JSON.stringify(choicesFromProducts);
        const allFields = [
          ...(interpreter.getSnapshot()?.context.fields || []),
          ...(interpreter.getSnapshot()?.context.externalFields || []),
        ];
        const indexFilters = allFields?.findIndex((f) =>
          f.name.includes('filters'),
        );
        if (indexFilters !== undefined && indexFilters > -1) {
          const decodedFilters = JSON.parse(
            allFields?.[indexFilters]?.value as string,
          );

          setSelectedFilters(choicesFromProducts);
          setVisibleProducts(filterProducts(productsFromApi, decodedFilters));

          if (Object.keys(decodedFilters).length === 1) {
            // showPriceMode already set as default in overview step
            service?.send([
              {
                type: 'CHANGE',
                target: { name: 'filters', value: encodedFilters },
              },
              {
                type: 'CHANGE',
                target: { name: 'defaultFilters', value: encodedFilters },
              },
            ]);
          }
        }
      })
      .catch((error) => {
        KiziLogger.logError({
          eventId: 0,
          member: 'OverviewPage',
          message: 'Error in fetchData',
          eventName: 'fetchDataError',
          data: {
            error,

            variables: { ...productsRequest.options },
          },
        });

        setState({
          ...state,
          productsLoading: false,
        });
      });
  }, [fields]);

  const productsWithReviews = React.useMemo(() => {
    if (isLoading || !reviews) {
      return visibleProducts ?? [];
    }
    return visibleProducts.map((product) => ({
      ...product,
      review: reviews.find(({ providerId }) => {
        return (providerId || [''])
          .map(String)
          .includes(String(product.provider.id));
      }),
      type: COMPARATOR_TYPE.ENERGY,
    }));
  }, [visibleProducts, isLoading, reviews]);

  const shouldShowCompare = React.useMemo((): boolean => {
    const compareProducts = visibleProducts.filter(
      (product) => product.compare === true,
    );

    return compareProducts.length > 0;
  }, [visibleProducts]);

  const handleCompareChange =
    (products) => (e: React.ChangeEvent<HTMLInputElement>, id: string) => {
      const productIndex = products.findIndex((product) => product.id === id);
      const selectedProducts = products.filter(function (product) {
        return product.compare === true;
      });

      const allowedLength = isMobile() ? 1 : 2;

      if (productIndex >= 0) {
        if (
          selectedProducts.length > allowedLength &&
          products[productIndex].compare === false
        )
          return;
        products[productIndex].compare =
          e != null ? e.target.checked : !products[productIndex].compare;
      }

      const saveCompareFieldValues = products.filter(function (product) {
        return product.compare === true;
      });

      fieldValueChange(
        'comparisonProducts',
        JSON.stringify(saveCompareFieldValues),
      );

      const allFields = interpreter.getSnapshot()?.context.fields;
      const indexFilters = allFields?.findIndex((f) => f.name === 'filters');
      if (indexFilters !== undefined && indexFilters > -1) {
        const decodedFilters = JSON.parse(
          allFields?.[indexFilters]?.value as string,
        );

        setVisibleProducts(filterProducts(products, decodedFilters));
      }
    };

  const renderImage = (image: string): any => {
    return (
      <>
        {image ? (
          <Div pt={10} pl={10} pb={15}>
            <Image
              alt={'logo-${comparableProduct.name}'}
              src={image}
              width={80}
            />
          </Div>
        ) : (
          <Div height={40} />
        )}
      </>
    );
  };

  const renderReview = (review?: Review): any => {
    return (
      <>
        {review ? (
          <RatingReviews display="block" data={review} pl={10} />
        ) : (
          <Div height={30} />
        )}
      </>
    );
  };

  const comparableProducts = React.useMemo(() => {
    return productsWithReviews.filter((product) => product.compare === true);
  }, [productsWithReviews]);

  const renderComparableProducts = (): any => {
    return comparableProducts.map((comparableProduct: EnergyProduct) => {
      const { id, image, initialValue, discount, priceLabel, review, total } =
        getProductProps(comparableProduct, selectedFilters['showPriceMode'][0]);
      return (
        <Div
          bg="white"
          key={id + '_comparable'}
          display="flex"
          justifyContent="space-between"
          variant="compareProduct"
          minWidth={220}
          maxWidth={311}
          height={114}
        >
          <Div
            display="flex"
            flexDirection="column"
            height={119}
            flexGrow={1}
            flexShrink={0}
            flexBasis={0}
          >
            {renderImage(image)}
            {renderReview(review)}
          </Div>
          <Div
            display="flex"
            flexDirection="column"
            height={119}
            flexGrow={1}
            flexShrink={1}
            flexBasis={0}
          >
            <Price
              pt={40}
              discount={discount}
              initialValue={initialValue}
              label={priceLabel}
              value={total}
              variant="priceCompare"
            />
          </Div>
          <Div pt={15} pr={10} pl={20}>
            <Icon
              data-testid="icon-close-compare"
              display="flex"
              fill="second.0"
              height={16}
              onClick={() => {
                onCompareClose(id);
              }}
              name="cancel"
              width={16}
              style={{
                cursor: 'pointer',
              }}
            />
          </Div>
        </Div>
      );
    });
  };

  useEffect(() => {
    setState({
      ...state,
      selected: currentSorting,
    });
  }, [currentSorting]);

  useEffect(() => {
    setState({
      ...state,
      productList:
        sortArrayByKey(
          productsWithReviews,
          SORTING_REFERENCE[currentSorting],
          SORTING_ASCENDING_ORDER[currentSorting],
        ) ?? [],
    });
  }, [currentSorting, productsWithReviews]);

  const onCompareClose = (id: string) => {
    const tempVisibleProducts = visibleProducts.map((product) => {
      if (product.id === id) product.compare = false;
      return product;
    });

    const saveCompareFieldValues = tempVisibleProducts.filter(
      function (product) {
        return product.compare === true;
      },
    );

    fieldValueChange(
      'comparisonProducts',
      JSON.stringify(saveCompareFieldValues),
    );
    setVisibleProducts(tempVisibleProducts);
  };

  const onCompare = React.useCallback(() => {
    if (wizardStepIndex !== undefined)
      service?.send({ type: 'GOTO', index: wizardStepIndex + 1 });
  }, [service]);

  const onMediaChange = (matches: boolean) => {
    if (matches && comparisonProducts.length > 2) {
      const id = comparisonProducts[comparableProducts.length - 1].id;
      onCompareClose(id);
    }
  };

  if (state?.productsLoading !== false) {
    return <OverviewPageLoader />;
  }

  return (
    <>
      <PageLayout {...props} content={content} sideNav={sideNav} />
      <Div
        display={shouldShowCompare ? 'flex' : 'none'}
        justifyContent="center"
        variant="modalCompareContainer"
      >
        <Div
          display="flex"
          variant="modalCompare"
          height={['84px', 'auto', 'auto']}
        >
          <Div
            alignItems="center"
            justifyContent={['center', null, 'start']}
            variant="productWrapperCompare"
          >
            <Desktop>
              {renderComparableProducts()}
              {comparableProducts.length < 2 ? (
                <Div
                  display={['none', 'none', 'flex']}
                  minWidth={220}
                  maxWidth={311}
                  height={114}
                  variant="compareEmptyProduct"
                />
              ) : undefined}
              {comparableProducts.length < 3 ? (
                <Div
                  display={['none', 'none', 'flex']}
                  minWidth={220}
                  maxWidth={311}
                  height={114}
                  variant="compareEmptyProduct"
                />
              ) : undefined}
            </Desktop>
            <Div
              display="flex"
              flexGrow={0}
              flexShrink={0}
              flexBasis={0}
              mt={[0, 0, 20]}
            >
              <Desktop>
                <Button
                  className="buildButton"
                  variant="primary"
                  onClick={() => onCompare()}
                  data-testid="desktop-compare"
                  disabled={comparableProducts.length < 2 ? true : false}
                >
                  {'Vergelijk'}
                </Button>
              </Desktop>
              <Mobile onChange={onMediaChange}>
                <Div
                  data-testid="media-change"
                  onClick={() => onMediaChange(true)}
                />
                <Button
                  className="buildButton"
                  variant="primary"
                  onClick={() => onCompare()}
                  data-testid="mobile-compare"
                  height={54}
                  disabled={comparableProducts.length < 2 ? true : false}
                >
                  {`Vergelijk ${comparableProducts.length}/2 geslecteerd`}
                </Button>
              </Mobile>
            </Div>
          </Div>
        </Div>
      </Div>
    </>
  );
};
